import { useEffect, useMemo } from 'react'

import type { VuiComponent } from '../core'
import type { ButtonProps } from './button.types'

import { useButtonGroupContext } from '../buttonGroup/context'
import { styled, vui } from '../core'
import { filterUndefined } from '../utils'
import { renderButtonIcon, renderContent } from './buttonHelpers'
import ButtonIcon from './buttonIcon'
import ButtonText from './buttonText'
import { CSS_VAR_MAPPINGS } from './consts'
import { ButtonProvider } from './context'
import { useButtonComputed } from './useButtonComputed'
import { resolveVariantKey, wrapWithVars } from './utils'

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------
const BUTTON_TYPE = 'button'
const FOCUS_RING_SIZE = 3

// ---------------------------------------------------------------------------
// Styled primitives
// ---------------------------------------------------------------------------
export const ButtonBase = styled.buttonBox<ButtonProps>`
  align-items: center;
  display: inline-flex;
  flex-shrink: 0;
  font-weight: var(--vui-button-font-weight, 500);
  justify-content: center;
  line-height: 1.2;
  overflow: hidden;
  position: relative;
  transition-duration: var(--vui-button-transition-duration, fast);
  user-select: none;
  width: fit-content;
`

// ---------------------------------------------------------------------------
// Button component
// ---------------------------------------------------------------------------

/**
 * Button Component
 *
 * A versatile button component that supports multiple states, sizes, variants, and behaviors.
 * Part of the VUI design system with built-in accessibility features.
 *
 */
export const Button = vui<'button', ButtonProps>((props, ref) => {
  const buttonGroupProps = useButtonGroupContext()
  const mergedProps = { ...buttonGroupProps, ...props }
  const {
    borderWidth: borderWidthProp,
    children,
    className,
    colorScheme, // deprecated — not forwarded to DOM
    disabled,
    dropDownIcon = 'uiAngleDown',
    intent: intentProp,
    isSplit: _isSplit, // deprecated — destructured to prevent DOM leak
    icon,
    startIcon,
    iconLeft,
    endIcon: endIconProp,
    iconRight,
    isActive = false,
    isDropDown,
    isElevated = false,
    isFullWidth = false,
    isLoading = false,
    loadingText = 'Loading',
    isRound = false,
    isTruncated = false,
    size,
    text,
    title,
    variant: variantProp,
    'aria-label': ariaLabel,
    'aria-labelledby': ariaLabelledby,
    onClick,
    ...rest
  } = mergedProps

  // Non-memoized calculations (trivial expressions — no memo benefit)
  const hasTextContent = Boolean(children || text)
  const isIconOnly = !children && !text && !isDropDown
  const resolvedStartIcon = startIcon ?? iconLeft
  const resolvedEndIconProp = endIconProp ?? iconRight
  const hasStartIcon = Boolean(resolvedStartIcon || icon || isLoading)
  const hasEndIcon = Boolean(resolvedEndIconProp || isDropDown)
  const endIcon = isDropDown ? dropDownIcon : resolvedEndIconProp

  const variantForTheme = useMemo(
    () => filterUndefined({ variant: resolveVariantKey(variantProp, intentProp) }),
    [variantProp, intentProp],
  )

  // Consolidated computation of all button styles and props
  const computed = useButtonComputed({
    size,
    variant: variantForTheme,
    intentProp,
    borderWidthProp,
    disabled: disabled ?? false,
    isActive,
    isIconOnly,
    isRound,
    isElevated,
    hasStartIcon,
    hasEndIcon,
    isFullWidth,
    className,
    variantProp,
  })

  // Memoized context value
  const contextValue = useMemo(
    () =>
      filterUndefined({
        disabled,
        size,
        variant: variantForTheme.variant,
        intent: intentProp,
        isDropDown,
        hasTextContent,
        isLoading,
      }),
    [disabled, size, variantForTheme.variant, intentProp, isDropDown, hasTextContent, isLoading],
  )

  // Memoized final props - using primitives to avoid object reference changes
  const finalVariant = variantForTheme.variant as string | undefined

  // Extract primitive values for stable dependencies
  const borderWidth = computed.spacing.borderWidth
  const plValue = computed.spacing.pl
  const prValue = computed.spacing.pr
  const ptValue = computed.spacing.pt
  const pbValue = computed.spacing.pb
  const gapValue = computed.spacing.gap

  // Wrap props with CSS variables for customization.
  // Uses module-level CSS_VAR_MAPPINGS and wrapWithVars for stable references.
  const propsWithVars = useMemo(
    () => ({
      ...wrapWithVars(computed.buttonStyles, CSS_VAR_MAPPINGS.base),
      ...wrapWithVars(computed.activeProps, CSS_VAR_MAPPINGS.active),
      ...wrapWithVars(computed.disabledProps, CSS_VAR_MAPPINGS.disabled),
    }),
    [computed.buttonStyles, computed.activeProps, computed.disabledProps],
  )

  // Stable props memoized without `rest` so the memo can actually reuse between renders.
  // `rest` (a new object every render) is spread in separately below.
  const stableFinalProps = useMemo(
    () => ({
      borderWidth: `var(--vui-button-border-width, ${borderWidth}px)`,
      className: computed.buttonClassName,
      disabled,
      'aria-label': isLoading && isIconOnly ? loadingText || 'Loading' : ariaLabel,
      'aria-labelledby': ariaLabelledby,
      'aria-busy': isLoading ? true : undefined,
      'aria-disabled': isLoading ? true : undefined,
      'aria-haspopup': isDropDown ? true : undefined,
      focusRing: FOCUS_RING_SIZE,
      gap: gapValue,
      pl: plValue,
      pr: prValue,
      pt: ptValue,
      pb: pbValue,
      onClick: isLoading ? undefined : onClick,
      pointerEvents: isLoading ? ('none' as const) : undefined,
      ref,
      title: title || undefined,
      type: BUTTON_TYPE as 'button',
      variant: finalVariant,
      ...propsWithVars,
      ...computed.aliasedProps,
    }),
    [
      borderWidth,
      plValue,
      prValue,
      ptValue,
      pbValue,
      gapValue,
      computed.buttonClassName,
      computed.aliasedProps,
      disabled,
      ariaLabel,
      ariaLabelledby,
      isDropDown,
      ref,
      title,
      isLoading,
      onClick,
      finalVariant,
      isIconOnly,
      loadingText,
      propsWithVars,
    ],
  )
  const finalProps = { ...stableFinalProps, ...rest }

  // Consolidate all dev-mode warnings into a single effect so they run after all hooks
  useEffect(() => {
    if (process.env.NODE_ENV !== 'production') {
      if (_isSplit !== undefined) {
        console.warn('[Button] The `isSplit` prop is deprecated and has no effect. It will be removed in VUI 5.x.')
      }
      if (colorScheme !== undefined) {
        console.warn('[Button] `colorScheme` is deprecated. Use `variant` + `intent` instead.')
      }
      if (iconLeft !== undefined) {
        console.warn('[Button] `iconLeft` is deprecated. Use `startIcon` instead.')
      }
      if (iconRight !== undefined) {
        console.warn('[Button] `iconRight` is deprecated. Use `endIcon` instead.')
      }
      if (
        variantProp !== undefined &&
        !['primary', 'secondary', 'tertiary'].includes(variantProp) &&
        /^(primary|secondary|tertiary)(Brand|Contrast|Success|Danger|Warning)$/.test(variantProp)
      ) {
        console.warn(
          `[Button] Combined variant "${variantProp}" is deprecated. ` +
            'Use separate `variant` + `intent` props instead (e.g. variant="primary" intent="brand").',
        )
      }
      const isIconOnly = !children && !text
      const hasLabel = Boolean(ariaLabel || ariaLabelledby || title)
      if (isIconOnly && !hasLabel) {
        console.warn(
          '[Button] Icon-only buttons must have an accessible label. ' +
            'Add `aria-label`, `aria-labelledby`, or `title` to describe the button action.',
        )
      }
    }
  }, [_isSplit, colorScheme, iconLeft, iconRight, variantProp, children, text, title, ariaLabel, ariaLabelledby])

  const leftIcon = renderButtonIcon(resolvedStartIcon ?? icon, 'left', isLoading)
  const rightIcon = renderButtonIcon(endIcon, 'right')
  // Icon-only buttons: suppress loadingText as visible content — it is set as aria-label above.
  const contentElement = isLoading
    ? isIconOnly
      ? null
      : renderContent(loadingText, undefined, isTruncated)
    : renderContent(children, text, isTruncated)

  return (
    <ButtonProvider value={contextValue}>
      <ButtonBase {...finalProps}>
        {leftIcon}
        {contentElement}
        {rightIcon}
      </ButtonBase>
    </ButtonProvider>
  )
}) as VuiComponent<'button', ButtonProps> & {
  Icon: typeof ButtonIcon
  Text: typeof ButtonText
}

Button.Icon = ButtonIcon
Button.Text = ButtonText
Button.displayName = 'Button'

export default Button
